import { Component, Input, OnInit } from '@angular/core';
import { ReportsService, ReportSubscription } from '../reports-service';
import {
  ReportsSetupFormService,
  ReportFormData,
} from '../reports-setup-form-service';
import { AuthService } from 'src/app/service/auth.service';
import { YukkApi } from 'src/app/service/yukkapi.service';
import { BehaviorSubject } from 'rxjs';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

@Component({
  selector: 'app-reports-settings',
  templateUrl: './reports-settings.component.html',
  styleUrls: ['./reports-settings.component.scss'],
})
export class ReportsSettingsComponent implements OnInit {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() scoreOptions: any[] = [];
  subscriptions: ReportSubscription[] = [];

  loadingReports = false;
  currentReport: ReportFormData;
  private reportListSubject = new BehaviorSubject<ReportFormData[]>([]);
  reportList$ = this.reportListSubject.asObservable();

  constructor(
    private reportsService: ReportsService,
    private reportFormService: ReportsSetupFormService,
    private reportService: ReportsService,
    private yukkaApi: YukkApi,
    private auth: AuthService,
    public snackBar: MatSnackBar,
  ) {
    this.currentReport = this.reportFormService.getCurrentReport();
  }

  ngOnInit() {
    this.loadingReports = true;

    this.reportsService.subscriptions$.subscribe((subscriptions) => {
      if (subscriptions && subscriptions.length > 0) {
        this.subscriptions = subscriptions;
        this.generateReportList();
      }
    });

    this.reportList$.subscribe((reportList) => {
      if (reportList.length > 0) {
        this.reportFormService.setupReportFromSubscription(reportList[0]);
      }
    });

    this.loadSubscriptions();

    this.reportFormService.currentReport$.subscribe((report) => {
      this.currentReport = report;
    });
  }

  selectReport(report: ReportFormData) {
    this.reportFormService.setupReportFromSubscription(report);
  }

  loadSubscriptions() {
    this.reportsService.getAllSubscriptions().subscribe({
      next: (data) => {
        if (data && data.length > 0) {
          this.subscriptions = data;

          this.generateReportList();
        }
      },
      error: (error) => console.error('Error fetching subscriptions', error),
      complete: () => {
        this.loadingReports = false;
      },
    });
  }

  createReport() {
    this.reportFormService.createNewReport();
  }

  reportStatusUpdate(value: boolean, report: ReportFormData) {
    const updatedReport = {
      ...report,
      active: value,
      isNew: false,
    };
    const reportStatusMesssage = value ? 'activated' : 'deactivated';
    const newSubscription =
      this.reportFormService.convertFormToSubscription(updatedReport);

    this.reportService.updateSubscriptionStatus(newSubscription).subscribe({
      next: () => {
        const currentList = this.reportListSubject.getValue();
        const updatedList = currentList.map((r) =>
          r.uid === report.uid ? { ...r, active: value } : r,
        );
        this.reportListSubject.next(updatedList);
        this.reportFormService.setupReportFromSubscription(report);
      },
      complete: () => {
        this.snackBar.open(
          'Report Subscription ' + reportStatusMesssage + '.',
          'OK',
          {
            duration: 5000,
          },
        );
      },
      error: (error) => {
        console.error('Error updating subscription status', error);
        this.snackBar.open(
          'Error updating subscription status. Please try again.',
          'OK',
          {
            duration: 5000,
          },
        );
      },
    });
  }

  private generateReportList() {
    if (!this.subscriptions || this.subscriptions.length === 0) {
      console.warn('No subscriptions available to generate report list.');
      return;
    }

    const reportList: ReportFormData[] = [];

    const entityRequests = this.subscriptions.map((subscription) => {
      const displayableParts = subscription.displayable.split(':');
      const displayableType = displayableParts[2];
      const id = displayableParts.slice(3).join(':');

      if (displayableType === 'entity') {
        return this.yukkaApi
          .getEntityName([id])
          .toPromise()
          .then((companyName) => {
            reportList.push({
              uid: subscription.uid,
              reportType: displayableType,
              reportObject: id,
              entityName: companyName,
              active: subscription.active,
              schedule: subscription.schedule,
              score: subscription.score,
              recipients: subscription.recipients,
              isNew: false,
            });
          });
      } else if (displayableType === 'portfolio') {
        return this.auth
          .portFolios()
          .toPromise()
          .then((result) => {
            const portfolio = result.find((item) => item.uid === id);
            if (portfolio) {
              reportList.push({
                uid: subscription.uid,
                reportType: displayableType,
                reportObject: id,
                portfolioName: portfolio.name,
                active: subscription.active,
                schedule: subscription.schedule,
                score: subscription.score,
                recipients: subscription.recipients,
                isNew: false,
              });
            }
          });
      }
      return Promise.resolve();
    });

    Promise.all(entityRequests).then(() => {
      this.reportListSubject.next(reportList);
    });
  }

  getScoreLabel(score: string): string {
    return (
      this.scoreOptions.find((option) => option.value === score)?.label ?? ''
    );
  }

  setReportTitle(report): string {
    if (report.reportType === 'portfolio') {
      return `${report.portfolioName} - ${this.getScoreLabel(report.score)}`;
    } else if (report.reportType === 'entity') {
      return ` ${report.entityName} - ${this.getScoreLabel(report.score)}`;
    }
  }
}
